import { NumberInput, Paper, Select, Stack, Switch, Text, TextInput, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { FontPreset, LabelElement, LabelType } from "../../api/types";
import { ALLOWED_FIELDS, FONT_PRESETS, ROTATIONS } from "./constants";

interface ElementInspectorProps {
  labelType: LabelType;
  element: LabelElement | null;
  onChange: (patch: Partial<LabelElement>) => void;
}

export function ElementInspector({ labelType, element, onChange }: ElementInspectorProps) {
  const { t } = useTranslation();
  if (!element) {
    return (
      <Paper withBorder p="md">
        <Text size="sm" c="dimmed">{t("labelEditor.selectElement")}</Text>
      </Paper>
    );
  }

  const fontPreset = element.kind === "text"
    ? FONT_PRESETS.find((p) => p.value === element.font)
    : null;
  const fieldOptions = ALLOWED_FIELDS[labelType].map((f) => ({ value: f, label: f }));

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Title order={5}>
          {element.kind === "qr" ? "QR" : t("labelEditor.text")}
        </Title>

        {element.kind === "text" && (
          <Select
            label={t("labelEditor.source")}
            data={[
              { value: "static", label: t("labelEditor.sourceStatic") },
              { value: "field",  label: t("labelEditor.sourceField") },
            ]}
            value={element.source}
            onChange={(v) => v && onChange({
              source: v as "static" | "field",
              content: v === "static" ? (element.content ?? "Text") : null,
              field: v === "field" ? (element.field ?? fieldOptions[0]?.value ?? null) : null,
            })}
            allowDeselect={false}
          />
        )}

        {element.kind === "text" && element.source === "static" && (
          <TextInput
            label={t("labelEditor.staticText")}
            value={element.content ?? ""}
            onChange={(e) => onChange({ content: e.currentTarget.value })}
          />
        )}

        {element.source === "field" && (
          <Select
            label={t("labelEditor.field")}
            data={fieldOptions}
            value={element.field}
            onChange={(v) => v && onChange({ field: v })}
            allowDeselect={false}
          />
        )}

        <NumberInput
          label="X (dots)"
          value={element.x}
          min={0}
          onChange={(v) => onChange({ x: typeof v === "number" ? v : 0 })}
        />
        <NumberInput
          label="Y (dots)"
          value={element.y}
          min={0}
          onChange={(v) => onChange({ y: typeof v === "number" ? v : 0 })}
        />
        <NumberInput
          label={t("labelEditor.scale")}
          value={element.scale}
          min={1}
          max={element.kind === "qr" ? 10 : 30}
          onChange={(v) => onChange({ scale: typeof v === "number" ? v : 1 })}
        />

        {element.kind === "text" && (
          <>
            <Select
              label={t("labelEditor.font")}
              data={FONT_PRESETS.map((p) => ({ value: p.value, label: t(p.labelKey) }))}
              value={element.font}
              onChange={(v) => v && onChange({ font: v as FontPreset })}
              allowDeselect={false}
            />
            <Switch
              label={t("labelEditor.bold")}
              checked={element.bold}
              disabled={!fontPreset?.supportsBold}
              onChange={(e) => onChange({ bold: e.currentTarget.checked })}
            />
            <Select
              label={t("labelEditor.rotation")}
              data={ROTATIONS.map((r) => ({ value: String(r.value), label: r.label }))}
              value={String(element.rotation)}
              onChange={(v) => v && onChange({ rotation: Number(v) as 0 | 90 | 180 | 270 })}
              allowDeselect={false}
            />
          </>
        )}
      </Stack>
    </Paper>
  );
}
